import React, { useState, useMemo } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import {
  Container, Box, Paper, Typography, Avatar, Stack, Button, Card, CardActionArea, CardContent, Chip, Tabs, Tab, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Menu, MenuItem, IconButton,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import FavoriteIcon from '@mui/icons-material/Favorite'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlaceIcon from '@mui/icons-material/Place'
import GroupsIcon from '@mui/icons-material/Groups'
import BusinessIcon from '@mui/icons-material/Business'
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LocationCard from '../../components/LocationCard'
import { WishlistSummary, createWishlist, updateWishlist, deleteWishlist } from '../../lib/wishlists'

interface User {
  id: number
  slug: string
  name: string
  bio: string | null
  avatar_url: string | null
  member_since: string
}

interface Stats {
  wishlist: number
  visited: number
  locations: number
  groups: number
  companies: number
}

interface GroupRef { id: number; name: string }
interface LocationItem {
  id: number
  slug?: string | null
  name: string
  address: string | null
  city?: string | null
  country?: string | null
  category: string | null
  google_place_id?: string | null
  thumbnail_url?: string | null
  groups?: GroupRef[]
  wishlisted_at?: string | null
  visited_at?: string | null
}
interface CompanyItem { id: number; name: string; country: string | null }
interface GroupItem { id: number; name: string; company: string | null; gender: string; group_type: string }

interface Props {
  user: User
  is_me: boolean
  stats: Stats
  locations: LocationItem[]
  companies: CompanyItem[]
  groups: GroupItem[]
  wishlist: LocationItem[]
  visited: LocationItem[]
  suggestions: LocationItem[]
  wishlists: WishlistSummary[]
}

interface SharedProps {
  current_user: { id: number } | null
}

type TabKey = 'saved' | 'visited' | 'locations' | 'groups' | 'companies'

export default function UserShow({
  user, is_me, stats, locations, companies, groups, wishlist, visited, suggestions, wishlists,
}: Props) {
  const page = usePage<SharedProps>()
  const isMe = is_me ?? page.props.current_user?.id === user.id

  const [tab, setTab] = useState<TabKey>('saved')
  const [activeWishlistId, setActiveWishlistId] = useState<number | 'all'>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [editTarget, setEditTarget] = useState<WishlistSummary | null>(null)
  const [editName, setEditName] = useState('')
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; wishlist: WishlistSummary } | null>(null)

  const filteredWishlist = useMemo(() => {
    if (activeWishlistId === 'all') return wishlist
    const target = wishlists.find((w) => w.id === activeWishlistId)
    if (!target) return wishlist
    const ids = new Set(target.location_ids)
    return wishlist.filter((l) => ids.has(l.id))
  }, [activeWishlistId, wishlist, wishlists])

  const refreshWishlists = () => router.reload({ only: ['wishlists', 'wishlists_summary'] })

  const handleCreate = async () => {
    const name = createName.trim()
    if (!name) return
    setSubmitting(true)
    setCreateError(null)
    const created = await createWishlist(name)
    if (!created) {
      setCreateError('Could not create list (name may be taken)')
      setSubmitting(false)
      return
    }
    setCreateName('')
    setCreateOpen(false)
    setSubmitting(false)
    refreshWishlists()
  }

  const handleRename = async () => {
    if (!editTarget) return
    const name = editName.trim()
    if (!name) return
    setSubmitting(true)
    const updated = await updateWishlist(editTarget.id, name)
    setSubmitting(false)
    if (updated) {
      setEditTarget(null)
      setEditName('')
      refreshWishlists()
    }
  }

  const handleDelete = async (wishlistId: number) => {
    if (!confirm('Delete this list? Locations remain in your general saved list.')) return
    const ok = await deleteWishlist(wishlistId)
    if (ok) {
      if (activeWishlistId === wishlistId) setActiveWishlistId('all')
      refreshWishlists()
    }
  }

  const statItems: Array<{ key: TabKey; label: string; value: number; icon: React.ReactNode }> = [
    { key: 'saved', label: 'Saved', value: stats.wishlist, icon: <FavoriteIcon fontSize="small" /> },
    { key: 'visited', label: 'Visited', value: stats.visited, icon: <CheckCircleIcon fontSize="small" /> },
    { key: 'locations', label: 'Added', value: stats.locations, icon: <PlaceIcon fontSize="small" /> },
    { key: 'groups', label: 'Groups', value: stats.groups, icon: <GroupsIcon fontSize="small" /> },
    { key: 'companies', label: 'Companies', value: stats.companies, icon: <BusinessIcon fontSize="small" /> },
  ]

  return (
    <>
      <Head title={`${user.name}'s profile`} />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        {/* Hero header */}
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #7B5BD6 0%, #9C7AE6 100%)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Avatar
              src={user.avatar_url || undefined}
              sx={{
                width: { xs: 88, sm: 112 },
                height: { xs: 88, sm: 112 },
                fontSize: '2.4rem',
                fontWeight: 700,
                border: '4px solid rgba(255,255,255,0.85)',
                color: 'primary.main',
                backgroundColor: '#fff',
              }}
            >
              {user.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'inherit' }}>
                {user.name}
              </Typography>
              {user.bio && (
                <Typography
                  variant="body1"
                  sx={{ color: 'rgba(255,255,255,0.9)', whiteSpace: 'pre-wrap', mt: 0.5 }}
                >
                  {user.bio}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', mt: 0.75 }}>
                Member since {user.member_since}
              </Typography>
            </Box>
            {isMe && (
              <Button
                component={Link}
                href="/users/edit"
                startIcon={<EditIcon />}
                variant="contained"
                size="small"
                sx={{
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  flexShrink: 0,
                  backgroundColor: '#fff',
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                }}
              >
                Edit profile
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Stats row */}
        <Paper sx={{ p: { xs: 1, md: 1.5 }, mb: 3, borderRadius: 3 }}>
          <Stack
            direction="row"
            sx={{
              overflowX: 'auto',
              gap: { xs: 0.5, md: 1 },
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {statItems.map((s) => {
              const active = tab === s.key
              return (
                <Box
                  key={s.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => setTab(s.key)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTab(s.key) }}
                  aria-label={`${s.label}: ${s.value}`}
                  sx={{
                    flex: '1 1 0',
                    minWidth: 88,
                    cursor: 'pointer',
                    textAlign: 'center',
                    py: 1.5,
                    px: 1,
                    borderRadius: 2,
                    color: active ? 'primary.main' : 'text.secondary',
                    backgroundColor: active ? 'rgba(123,91,214,0.10)' : 'transparent',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { backgroundColor: 'rgba(123,91,214,0.06)' },
                  }}
                >
                  <Box sx={{ mb: 0.25, display: 'flex', justifyContent: 'center' }}>{s.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>{s.label}</Typography>
                </Box>
              )
            })}
          </Stack>
        </Paper>

        {/* Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v as TabKey)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Profile sections"
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 },
            }}
          >
            <Tab value="saved" label={`Saved (${stats.wishlist})`} />
            <Tab value="visited" label={`Visited (${stats.visited})`} />
            <Tab value="locations" label={`Added (${stats.locations})`} />
            <Tab value="groups" label={`Groups (${stats.groups})`} />
            <Tab value="companies" label={`Companies (${stats.companies})`} />
          </Tabs>
        </Box>

        {/* Tab panels */}
        <Box>
          {tab === 'saved' && (
            <>
              {isMe && (
                <Stack
                  direction="row"
                  sx={{
                    mb: 2,
                    gap: 1,
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  <Chip
                    label={`All saved (${wishlist.length})`}
                    color={activeWishlistId === 'all' ? 'primary' : 'default'}
                    variant={activeWishlistId === 'all' ? 'filled' : 'outlined'}
                    onClick={() => setActiveWishlistId('all')}
                    sx={{ flexShrink: 0 }}
                  />
                  {wishlists.map((w) => (
                    <Chip
                      key={w.id}
                      label={`${w.name} (${w.items_count})`}
                      color={activeWishlistId === w.id ? 'primary' : 'default'}
                      variant={activeWishlistId === w.id ? 'filled' : 'outlined'}
                      onClick={() => setActiveWishlistId(w.id)}
                      onDelete={(e) => {
                        e.stopPropagation()
                        setMenuAnchor({ el: e.currentTarget as HTMLElement, wishlist: w })
                      }}
                      deleteIcon={<MoreVertIcon aria-label={`Manage list ${w.name}`} />}
                      sx={{ flexShrink: 0 }}
                    />
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() => setCreateOpen(true)}
                    sx={{ flexShrink: 0 }}
                  >
                    New list
                  </Button>
                </Stack>
              )}
              <TabPanel
                empty={
                  activeWishlistId === 'all'
                    ? `${user.name} has no saved locations yet.`
                    : 'This list is empty. Add locations from their detail pages.'
                }
                count={filteredWishlist.length}
              >
                <CardGrid>
                  {filteredWishlist.map((l) => {
                    const names = wishlists
                      .filter((w) => w.location_ids.includes(l.id))
                      .map((w) => w.name)
                    return (
                      <LocationCard
                        key={`wish-${l.id}`}
                        location={l}
                        layout="horizontal"
                        showActions={false}
                        wishlistNames={names}
                      />
                    )
                  })}
                </CardGrid>
              </TabPanel>
            </>
          )}

          {tab === 'visited' && (
            <TabPanel
              empty={`${user.name} hasn't visited any locations yet.`}
              count={visited.length}
            >
              <CardGrid>
                {visited.map((l) => (
                  <LocationCard key={`vis-${l.id}`} location={l} layout="horizontal" showActions={false} />
                ))}
              </CardGrid>
            </TabPanel>
          )}

          {tab === 'locations' && (
            <TabPanel
              empty={`${user.name} hasn't added any locations yet.`}
              count={locations.length}
            >
              <CardGrid>
                {locations.map((l) => (
                  <Card key={l.id}>
                    <CardActionArea component={Link} href={`/locations/${l.slug || l.id}`}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 0.5 }}>{l.name}</Typography>
                        {l.address && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{l.address}</Typography>}
                        {l.category && <Chip label={l.category.replace(/_/g, ' ')} size="small" />}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </CardGrid>
            </TabPanel>
          )}

          {tab === 'groups' && (
            <TabPanel
              empty={`${user.name} hasn't added any groups yet.`}
              count={groups.length}
            >
              <CardGrid>
                {groups.map((g) => (
                  <Card key={g.id}>
                    <CardActionArea component={Link} href={`/groups/${g.id}`}>
                      <CardContent>
                        <Typography variant="h6">{g.name}</Typography>
                        {g.company && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{g.company}</Typography>}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Chip label={g.gender} size="small" />
                          <Chip label={g.group_type} size="small" variant="outlined" />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </CardGrid>
            </TabPanel>
          )}

          {tab === 'companies' && (
            <TabPanel
              empty={`${user.name} hasn't added any companies yet.`}
              count={companies.length}
            >
              <CardGrid>
                {companies.map((c) => (
                  <Card key={c.id}>
                    <CardActionArea component={Link} href={`/companies/${c.id}`}>
                      <CardContent>
                        <Typography variant="h6">{c.name}</Typography>
                        {c.country && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.country}</Typography>}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </CardGrid>
            </TabPanel>
          )}
        </Box>

        {isMe && suggestions.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Box>
              <Typography variant="h5" sx={{ mb: 2 }}>Suggested for you</Typography>
              <CardGrid>
                {suggestions.map((l) => (
                  <LocationCard key={`sug-${l.id}`} location={l} layout="horizontal" />
                ))}
              </CardGrid>
            </Box>
          </>
        )}

        {/* Create list dialog */}
        <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setCreateError(null) }} fullWidth maxWidth="xs">
          <DialogTitle>Create list</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="List name"
              placeholder="e.g. Trip to Seoul"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              inputProps={{ maxLength: 80 }}
              sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              error={!!createError}
              helperText={createError || ' '}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={submitting || !createName.trim()}>
              {submitting ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rename list dialog */}
        <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>Rename list</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="List name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              inputProps={{ maxLength: 80 }}
              sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditTarget(null)} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={handleRename} disabled={submitting || !editName.trim()}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Per-list context menu */}
        <Menu
          open={!!menuAnchor}
          anchorEl={menuAnchor?.el}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              if (!menuAnchor) return
              setEditTarget(menuAnchor.wishlist)
              setEditName(menuAnchor.wishlist.name)
              setMenuAnchor(null)
            }}
          >
            Rename
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!menuAnchor) return
              const id = menuAnchor.wishlist.id
              setMenuAnchor(null)
              handleDelete(id)
            }}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        </Menu>
      </Container>
    </>
  )
}

function TabPanel({ empty, count, children }: { empty: string; count: number; children: React.ReactNode }) {
  if (count === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{empty}</Typography>
      </Paper>
    )
  }
  return <>{children}</>
}

function CardGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
      }}
    >
      {children}
    </Box>
  )
}
